import { PageType } from 'src/apollo/sites'
import {
  LaunchDarklyFeatureFlag,
  LaunchDarklyFlags
} from 'shared/components/common/feature_flags/featureFlags'

declare const IS_NEXTJS: boolean

export enum RouteOwner {
  NEXTJS = 'nextjs',
  EXPRESS = 'express',
  UNKNOWN = 'unknown'
}

/**
 * Tier 3: Page routes that Next.js handles when the LD flag is ON, each paired
 * with the per-restaurant flag that decides whether Next.js *will* serve it.
 *
 * Add migrated routes here as they move from Express to Next.js — this is the
 * single source of truth. NEXTJS_PAGE_ROUTES and the route→flag lookup are both
 * derived from it, so a route can't exist without a flag or vice versa. Use
 * `flag: undefined` only for a route Next.js serves unconditionally.
 *
 * Order is load-bearing: entries are matched top-down, so /online/:slug/account
 * has to resolve before the broader Express prefixes get a chance.
 */
const NEXTJS_PAGE_ROUTE_FLAGS: Array<{
  route: string | RegExp
  flag: keyof LaunchDarklyFlags | undefined
}> = [
  { route: '/account', flag: 'ooSitesNextjsAccountPage' },
  { route: '/confirm', flag: 'ooSitesNextjsConfirmPage' },
  // iframe embed route (Express served /v1/iframe + /sites-web/v1/iframe).
  // Listed in post-strip form: callers normalise a leading /vN/ away before
  // matching (server-https.ts on the request path, normalizePathname here), so
  // /v1/iframe → /iframe.
  { route: '/iframe', flag: 'ooSitesNextjsIframePage' },
  // Only the account sub-page under the BOO /online/:slug context — not
  // checkout/confirm/order.
  {
    route: /^\/online\/[^/]+\/account(\/|$)/,
    flag: 'ooSitesNextjsAccountPage'
  }
]

// Prefix strings are matched with startsWith; RegExp entries are tested directly.
export const NEXTJS_PAGE_ROUTES: Array<string | RegExp> =
  NEXTJS_PAGE_ROUTE_FLAGS.map(({ route }) => route)

// Tier 3: Routes that ALWAYS go directly to Express, bypassing Next.js entirely.
// Evaluated AFTER NEXTJS_PAGE_ROUTES so migrated sub-paths (e.g./online/:slug/account)
// are caught by step B before the broader Express prefixes
// (/local, /online) match here in step C.
export const EXPRESS_PAGE_ROUTES: Array<string | RegExp> = [
  // Root-level assets served by Express middleware/router
  '/favicon.ico',
  '/robots.txt',
  '/tracking',

  // Express-only server utility endpoints
  '/generateSitemap',
  '/sitemaps',
  '/local/sitemaps',
  '/.well-known',

  // Express versioned path aliases (leading /vN/ strip doesn't cover these)
  '/sites-web',

  // Embed / widget pages
  '/map',
  '/adyen',
  '/iframe',

  // SitesRoutes — restaurant custom domain React Router pages
  '/brandedAppRedirect',
  '/menu',
  '/order',
  '/checkout',

  // ToastLocalRoutes — www.toasttab.com/local
  '/local',

  // ToastOrderRoutes — order.toasttab.com/online
  // NOTE: /online/:slug/account is in NEXTJS_PAGE_ROUTES and is caught first.
  '/online',

  // ToastAppOrderRoutes — toast.app / book.toasttab.com
  '/r'
]

/**
 * Matches a pathname against one route list entry.
 *
 * A string entry matches the path itself or a segment below it — note the
 * trailing slash, which is what keeps /orders from matching /order.
 */
const matchesRoute = (pathname: string, route: string | RegExp): boolean =>
  route instanceof RegExp
    ? route.test(pathname)
    : pathname === route || pathname.startsWith(route + '/')

export const isNextJsRoute = (pathname: string) => {
  return NEXTJS_PAGE_ROUTES.some((r) => matchesRoute(pathname, r))
}

export const isExpressRoute = (pathname: string) => {
  return EXPRESS_PAGE_ROUTES.some((r) => matchesRoute(pathname, r))
}

export function routeOwner(pathname: string): RouteOwner {
  // nextjs must be evaluated first so that /online/:slug/account can render properly
  if (isNextJsRoute(pathname)) {
    return RouteOwner.NEXTJS
  }

  if (isExpressRoute(pathname)) {
    return RouteOwner.EXPRESS
  }
  return RouteOwner.UNKNOWN
}

/**
 * The two apps that can actually be running or serving a page. Distinct from
 * RouteOwner, which also has UNKNOWN — a verdict about a path, never an app.
 */
export type RunningApp = RouteOwner.NEXTJS | RouteOwner.EXPRESS

/**
 * Which app the current bundle is running in.
 *
 * Guarded read: sites-web's CLIENT webpack bundle does not define IS_NEXTJS
 * (only its server bundle does), so a bare reference throws ReferenceError on
 * every Express page. Absent means "not Next.js", which is the safe answer —
 * it resolves to a full page load, and a full page load always reaches the
 * right app.
 */
const currentApp = (): RunningApp =>
  typeof IS_NEXTJS !== 'undefined' && IS_NEXTJS
    ? RouteOwner.NEXTJS
    : RouteOwner.EXPRESS

/** Strips query and hash so an href can be matched against the route lists. */
const pathnameOf = (href: string): string => {
  const end = href.search(/[?#]/)
  return end === -1 ? href : href.slice(0, end)
}

/**
 * Strips a leading version prefix, matching what the dual router does to the
 * request path before consulting the route lists (`server-https.ts`).
 *
 * Both sides have to normalise identically or they disagree: without this,
 * /v1/account is UNKNOWN here while the server resolves it to Next.js.
 */
const normalizePathname = (pathname: string): string =>
  pathname.replace(/^\/v\d+\//, '/')

/**
 * The pathname of an href the dual router can route, or null if it can't.
 *
 * Path-only hrefs are used as-is. Absolute and protocol-relative hrefs are
 * resolved against the current origin, so a same-origin absolute link (what
 * restaurant custom domains and the account link produce) still gets a
 * boundary verdict instead of being written off as external. Anything
 * off-origin, or not a URL at all (tel:, mailto:, #hash, bare relative), is
 * null — not ours to route.
 */
const internalPathOf = (href: string): string | null => {
  // Empty string and bare fragment links (#hash) are within-page anchors —
  // they don't navigate between apps, so there's no boundary to cross.
  if (!href || href.startsWith('#')) return null
  if (href.startsWith('/') && !href.startsWith('//')) {
    return normalizePathname(pathnameOf(href))
  }
  if (typeof window === 'undefined') return null

  try {
    const url = new URL(href, window.location.href)
    if (url.origin !== window.location.origin) return null
    // Reject non-http(s) same-origin schemes (e.g. a bare `mailto:` resolves
    // with an opaque origin, but be explicit rather than rely on that).
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    return normalizePathname(url.pathname)
  } catch {
    return null
  }
}

/**
 * Maps a CMS page type to the per-restaurant flag that gates Next.js serving it.
 *
 * Add one entry here when a new CMS page type is migrated to Next.js.
 * `camelCase` is used by client-side callers (useFlags returns camelCase keys).
 * `kebabCase` is used by server-side callers (ldClient.variation, commonData.flags).
 */
export const PAGE_TYPE_FLAGS: Partial<
  Record<
    string,
    {
      camelCase: keyof LaunchDarklyFlags
      kebabCase: LaunchDarklyFeatureFlag
    }
  >
> = {
  [PageType.Form]: {
    camelCase: 'ooSitesNextjsFormPage',
    kebabCase: LaunchDarklyFeatureFlag.OO_SITES_NEXTJS_FORM_PAGE
  },
  [PageType.File]: {
    camelCase: 'ooSitesNextjsFilePage',
    kebabCase: LaunchDarklyFeatureFlag.OO_SITES_NEXTJS_FILE_PAGE
  },
  [PageType.GiftCardPurchase]: {
    camelCase: 'ooSitesNextjsGiftCardPurchasePage',
    kebabCase: LaunchDarklyFeatureFlag.OO_SITES_NEXTJS_GIFT_CARD_PURCHASE_PAGE
  },
  [PageType.LoyaltySignUp]: {
    camelCase: 'ooSitesNextjsLoyaltyPage',
    kebabCase: LaunchDarklyFeatureFlag.OO_SITES_NEXTJS_LOYALTY_PAGE
  }
}

/**
 * Which app serves `pathname`, given the restaurant's flags.
 */
export const getServingApp = (
  pathname: string,
  flags: Partial<LaunchDarklyFlags> | undefined,
  additionalPages?: ReadonlyArray<{ route: string; pageType: string }>
): RunningApp => {
  const staticEntry = NEXTJS_PAGE_ROUTE_FLAGS.find(({ route }) =>
    matchesRoute(pathname, route)
  )
  if (staticEntry !== undefined) {
    if (staticEntry.flag === undefined) return RouteOwner.NEXTJS
    return flags?.[staticEntry.flag] ? RouteOwner.NEXTJS : RouteOwner.EXPRESS
  }

  if (additionalPages) {
    const page = additionalPages.find(({ route }) =>
      matchesRoute(pathname, route.startsWith('/') ? route : '/' + route)
    )
    if (page) {
      const flag = PAGE_TYPE_FLAGS[page.pageType]
      if (flag !== undefined) {
        return flags?.[flag.camelCase] ? RouteOwner.NEXTJS : RouteOwner.EXPRESS
      }
    }
  }

  return RouteOwner.EXPRESS
}

/**
 * Does navigating to `href` leave the app that's currently running?
 *
 * Callers use this to choose the navigation mechanism: a client-side transition
 * within one app, or a full page load across the Express/Next.js boundary.
 *
 * `flags` is the restaurant's LaunchDarkly flags, needed because whether Next.js
 * serves a migrated route is a per-restaurant decision. Null/undefined flag values
 * are treated as false (Express).
 *
 * `additionalPages` covers operator-defined CMS pages (FormPage etc.) whose
 * slugs can never appear in the static route lists.
 *
 * `currentlyRunningApp` is injectable for tests; it defaults to the real runtime.
 */
export const crossesAppBoundary = (
  href: string,
  flags: Partial<LaunchDarklyFlags> | undefined,
  additionalPages?: ReadonlyArray<{ route: string; pageType: string }>,
  currentlyRunningApp: RunningApp = currentApp()
): boolean => {
  const pathname = internalPathOf(href)
  // Not a path this router owns (off-origin, tel:, mailto:, bare #hash) — the
  // browser handles it, and a caller does the same thing with `false`.
  if (pathname === null) return false

  // Router-level kill switch. Evaluated from Express only.
  if (
    currentlyRunningApp !== RouteOwner.NEXTJS &&
    !flags?.ooSitesNextjsRouter
  ) {
    return false
  }

  const destination = getServingApp(pathname, flags, additionalPages)
  return destination !== currentlyRunningApp
}
